import { INITIAL_CANDIDATE, type BoardContents, type PublicProfile } from '@molt/shared';

/**
 * `disambiguateInitials` — separate avatar initials that clash on one board.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * WHY THE BROWSER TOUCHES INITIALS AT ALL
 * ───────────────────────────────────────────────────────────────────────────
 *
 * The server derives initials (`deriveInitials`, `apps/server/src/auth/profile.ts`)
 * one per whitespace word, so every one-word name gives one letter: "Benjamin"
 * and "Bert" both arrive as `B`. `initialsColour` is one of ten hashed colours,
 * so the two discs collide on colour often enough too, and an assignee row of
 * two identical `B`s does not say who is who — the owner's report.
 *
 * This is **not** a second derivation. The server value is kept wherever it is
 * already distinct; only members whose initials clash *within the roster the
 * caller passes* (one board, one table) get a different two-character label,
 * and the label is built from the server's own first initial plus a character
 * the shared `INITIAL_CANDIDATE` rule accepts. Outside a roster nothing
 * changes: `Avatar` draws `profile.initials`.
 *
 * ───────────────────────────────────────────────────────────────────────────
 * THE RULE
 * ───────────────────────────────────────────────────────────────────────────
 *
 *  1. Dedupe by `id`. Skip anyone with a photo (`avatarUrl !== null` — the
 *     photo is the identity) or with no initials (`''` — they draw a glyph).
 *  2. Sort by `id`, so the result never depends on the order the caller
 *     happened to assemble the roster in.
 *  3. Group by exact `initials`. A group of one keeps its server value — no
 *     map entry — and that value is **reserved**, so no clashing member can be
 *     relabelled onto it.
 *  4. Each member of a clashing group, in `id` order, takes the first free
 *     candidate from, where `F` is the first code point of their server
 *     initials:
 *       a. their server initials, if those are exactly two code points;
 *       b. `F` + each `INITIAL_CANDIDATE` code point of `displayName` after its
 *          first one, upper-cased;
 *       c. `F` + each `INITIAL_CANDIDATE` code point of `username` after its
 *          first one, likewise;
 *       d. `F` + `2`…`9`, then `F` + `A`…`Z`;
 *       e. any two-character combination of `A`…`Z` and `0`…`9`;
 *       f. `F` + a number counting up from 10 — unreachable in practice, and
 *          there only so the loop provably ends.
 *     "Upper-cased" means the first code point of the upper-case form: `ß`
 *     upper-cases to `SS`, and a label must stay one code point per position.
 *     Iteration is by code point, so an astral emoji is never split.
 *
 * Every label taken is added to the reserved set, so **no two members ever
 * share a label**. Worked examples, ids in this order:
 *
 * | roster | labels |
 * |---|---|
 * | Benjamin, Bert, Ben Edwards (`BE`), Bea, Bob | Ben Edwards `BE` (alone in its group, reserved); Benjamin `BN`, Bert `BR`, Bea `BA`, Bob `BO` |
 * | Benjamin, Bert, Bea, Bob | Benjamin `BE`, Bert `BR`, Bea `BA`, Bob `BO` |
 *
 * Returns only the overrides — `id → label` for members whose label differs
 * from the server's. A lone `Bob` stays `B` with no entry.
 *
 * ⚠ SUPERADMIN-SET INITIALS ARE NOT EXEMPT. `profile.initials` is the
 * `users.initials` override when a platform superadmin set one
 * (`PATCH /admin/users/:id/initials`, `0073_member_initials.sql`), and this
 * function cannot tell a pinned value from a derived one. So a pinned `AB`
 * that collides with another member's `AB` on the same roster is relabelled
 * like any other clash — deliberately: two identical discs on one card are the
 * defect this file exists to prevent, and the pin still shows wherever it is
 * unique. A pin that is unique on the roster is never touched (rule 3).
 */
export function disambiguateInitials(
  roster: readonly PublicProfile[],
): ReadonlyMap<string, string> {
  const byId = new Map<string, PublicProfile>();
  for (const member of roster) {
    if (member.avatarUrl !== null || member.initials === '') continue;
    if (!byId.has(member.id)) byId.set(member.id, member);
  }

  const sorted = [...byId.values()].sort((a, b) =>
    a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
  );

  const groups = new Map<string, PublicProfile[]>();
  for (const member of sorted) {
    const group = groups.get(member.initials);
    if (group) group.push(member);
    else groups.set(member.initials, [member]);
  }

  const taken = new Set<string>();
  for (const [initials, group] of groups) {
    if (group.length === 1) taken.add(initials);
  }

  const labels = new Map<string, string>();
  for (const member of sorted) {
    const group = groups.get(member.initials);
    if (!group || group.length === 1) continue;

    let label: string | undefined;
    for (const candidate of candidates(member)) {
      if (!taken.has(candidate)) {
        label = candidate;
        break;
      }
    }
    // `candidates` ends in an unbounded counter, so this cannot be undefined;
    // the guard is for the type checker.
    if (label === undefined) continue;

    taken.add(label);
    if (label !== member.initials) labels.set(member.id, label);
  }

  return labels;
}

const DIGITS = '23456789';
const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const ANY = `${LETTERS}0123456789`;

/** One code point, upper-cased, kept to one code point (`ß` → `S`, not `SS`). */
function upperOne(codePoint: string): string {
  return [...codePoint.toUpperCase()][0] ?? codePoint;
}

/** Every `INITIAL_CANDIDATE` code point of `text` after its first, upper-cased. */
function laterCandidates(text: string): string[] {
  const found: string[] = [];
  for (const codePoint of text) {
    if (INITIAL_CANDIDATE.test(codePoint)) found.push(upperOne(codePoint));
  }
  return found.slice(1);
}

/** The candidate labels for one clashing member, in preference order. */
function* candidates(member: PublicProfile): Generator<string> {
  const own = [...member.initials];
  const first = own[0] ?? '';

  if (own.length === 2) yield member.initials;
  for (const next of laterCandidates(member.displayName)) yield first + next;
  for (const next of laterCandidates(member.username)) yield first + next;
  for (const next of DIGITS) yield first + next;
  for (const next of LETTERS) yield first + next;
  for (const a of ANY) for (const b of ANY) yield a + b;
  for (let n = 10; ; n += 1) yield `${first}${n}`;
}

/**
 * The roster's identity for memoisation: everything `disambiguateInitials`
 * reads, and nothing else. A board's members and card members are rebuilt on
 * every optimistic update and every drag; keying on this string rather than on
 * the array means those produce the same labels object and re-render nothing.
 */
export function avatarRosterSignature(roster: readonly PublicProfile[]): string {
  return roster
    .map(
      (member) =>
        `${member.id}:${member.initials}:${member.avatarUrl === null ? 0 : 1}:${member.displayName}:${member.username}`,
    )
    .sort()
    .join('\n');
}

/**
 * Every profile that can draw as an avatar on one board: its member list
 * plus every card's members, deduplicated by `id` (first occurrence wins).
 *
 * Card members are included because the member list is signed-in only — an
 * anonymous reader of a public board has `members === undefined` and still
 * sees the card fronts — and because a card can carry someone the member
 * list no longer does. The order is irrelevant to the labels
 * (`disambiguateInitials` sorts by `id`) and to the memo
 * (`avatarRosterSignature` sorts its rows).
 */
export function boardAvatarRoster(
  members: readonly PublicProfile[] | undefined,
  contents: Pick<BoardContents, 'lists'> | undefined,
): PublicProfile[] {
  const groups: (readonly PublicProfile[])[] = [members ?? []];
  for (const list of contents?.lists ?? []) {
    for (const card of list.cards) groups.push(card.members);
  }
  return uniqueProfiles(groups);
}

/** The union of several profile lists, deduplicated by `id`. */
export function uniqueProfiles(
  groups: readonly (readonly PublicProfile[] | undefined)[],
): PublicProfile[] {
  const byId = new Map<string, PublicProfile>();
  for (const group of groups) {
    for (const profile of group ?? []) {
      if (!byId.has(profile.id)) byId.set(profile.id, profile);
    }
  }
  return [...byId.values()];
}
